// Wraps Base UI Tabs. Deviation: Up/Down move like Right/Left (TabBar card); tested.
import { Tabs } from '@base-ui/react/tabs'
import { type KeyboardEvent, type ReactNode, useEffect, useRef } from 'react'
import { cx } from '../cx'
import { Icon } from '../Icon/Icon'
import type { IconName } from '../Icon/icons'
import styles from './TabBar.module.css'

type TabBarTab = { id: string; label: string; icon?: IconName }

export function TabBar({
  tabs,
  active,
  onChange,
  label,
  className,
  children,
}: {
  tabs: readonly TabBarTab[]
  active: string
  onChange: (id: string) => void
  label: string
  className?: string
  children?: ReactNode
}) {
  const listRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // Re-run when the active tab changes so it scrolls itself into view.
    if (active === undefined) return
    listRef.current
      ?.querySelector<HTMLElement>('[role="tab"][aria-selected="true"]')
      ?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' })
  }, [active])

  function onListKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return
    event.preventDefault()
    const enabled = listRef.current?.querySelectorAll<HTMLElement>(
      '[role="tab"]:not([aria-disabled="true"])',
    )
    if (!enabled || enabled.length === 0) return
    const current = Array.from(enabled).indexOf(
      document.activeElement as HTMLElement,
    )
    const step = event.key === 'ArrowDown' ? 1 : -1
    const next = (current + step + enabled.length) % enabled.length
    enabled[next].focus()
    const id = tabs[next]?.id
    if (id !== undefined) onChange(id)
  }

  return (
    <Tabs.Root
      value={active}
      onValueChange={(v) => onChange(String(v))}
      className={cx(styles.root, className)}
    >
      <Tabs.List
        aria-label={label}
        activateOnFocus
        loopFocus
        className={styles.list}
        ref={listRef}
        onKeyDown={onListKeyDown}
      >
        {tabs.map((t) => (
          <Tabs.Tab key={t.id} value={t.id} className={styles.tab}>
            {t.icon && <Icon name={t.icon} size="icon-embedded" />}
            <span>{t.label}</span>
          </Tabs.Tab>
        ))}
      </Tabs.List>
      {children}
    </Tabs.Root>
  )
}

export function TabPanel({
  id,
  className,
  children,
}: {
  id: string
  className?: string
  children: ReactNode
}) {
  return (
    <Tabs.Panel value={id} className={className}>
      {children}
    </Tabs.Panel>
  )
}
